import { useState } from "react"
import { IconLoader2, IconLock } from "@tabler/icons-react"
import { toast } from "sonner"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { type BookingSeasonSnapshot, useCloseBookingSeasonNowMutation } from "../bookingSeasonApi"
import { closedReasonLabel, formatSeasonDateTime } from "../bookingSeasonFormat"
import { DateTimeField, issuesFor, type SeasonFieldsProps } from "./SeasonFields"

interface SeasonStatusCardProps extends SeasonFieldsProps {
  season: BookingSeasonSnapshot
  /** Called with the season as it stands after closing, so the form can follow it. */
  onClosed: (season: BookingSeasonSnapshot) => void
}

/**
 * Whether the public can book right now, why not, and who last changed it — and the booking window
 * itself: the two Pacific date-times between which the public may book. The dates are part of the
 * page's one season draft and are saved with the rest of it.
 */
export function SeasonStatusCard({ season, onClosed, form, issues, onChange }: SeasonStatusCardProps) {
  const [confirming, setConfirming] = useState(false)
  const [closeNow, { isLoading: closing }] = useCloseBookingSeasonNowMutation()

  const isOpen = season.status === "Open"
  const reason = closedReasonLabel(season)

  async function handleCloseNow() {
    try {
      const result = await closeNow().unwrap()
      onClosed(result)
      toast.success("Booking closed.", {
        description: "The public website and phone line are no longer taking new appointments.",
      })
    } catch (err) {
      const status = (err as { status?: number }).status
      toast.error(
        status === 403
          ? "You do not have permission to close booking."
          : "Could not close booking. Try again in a moment.",
      )
    } finally {
      setConfirming(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2">
          Public booking is
          <Badge variant={isOpen ? "default" : "destructive"}>{season.status}</Badge>
        </CardTitle>
        <CardDescription>
          {isOpen
            ? "The public website and phone line can book new appointments."
            : "The public website and phone line cannot book new appointments. Staff can still book, and anyone can manage an existing appointment."}
        </CardDescription>
        {isOpen && (
          <CardAction>
            <Button variant="destructive" size="sm" onClick={() => setConfirming(true)} disabled={closing}>
              <IconLock className="size-4" />
              Close booking now
            </Button>
          </CardAction>
        )}
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {reason && (
          <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-[max-content_1fr]">
            <dt className="text-muted-foreground">Reason</dt>
            <dd className="font-medium">{reason}</dd>
          </dl>
        )}
        <div className="grid gap-4 sm:grid-cols-2">
          <DateTimeField
            id="season-opens"
            label="Booking window opens (Pacific time)"
            hint="Public booking starts at this moment."
            value={form.opensAt}
            issues={issuesFor(issues, "opensAt")}
            onChange={(opensAt) => onChange({ opensAt })}
          />
          <DateTimeField
            id="season-closes"
            label="Booking window closes (Pacific time)"
            hint="Public booking stops at this moment."
            value={form.closesAt}
            issues={issuesFor(issues, "closesAt")}
            onChange={(closesAt) => onChange({ closesAt })}
          />
        </div>
        {season.createdBy && season.createdAt && (
          <p className="text-xs text-muted-foreground">
            Last changed by {season.createdBy} on {formatSeasonDateTime(season.createdAt)}
          </p>
        )}
      </CardContent>

      <AlertDialog open={confirming} onOpenChange={(open) => !open && !closing && setConfirming(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Close booking now?</AlertDialogTitle>
            <AlertDialogDescription>
              Public website and phone callers will immediately stop being able to book new appointments. Existing
              appointments are not affected. Staff can still book.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={closing}>Keep booking open</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={(e) => {
                e.preventDefault()
                void handleCloseNow()
              }}
              disabled={closing}
            >
              {closing && <IconLoader2 className="size-4 animate-spin" />}
              Close booking now
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  )
}
